import { ICurrency, ICurrencyFormatConf } from "./../model/ICurrency";
import ICurrencyLocalization from "./../model/ICurrencyLocalization";
import * as currency_config from "../../src/baseConfigs/currency-config.json";
import Big from "big.js";
import GameModel from "../model/GameModel";

/**
 * A helper class, which works with localized currencies.
 *
 * @class CurrencyUtils
 */
class CurrencyUtils {
    private specialCurrencies: string[] = ['BK.', 'BT.', 'FC.', 'GC.', 'SC.'];

    private get currencyDisplay(): string {
        try {
            // Not all browsers/OS supports the narrowSymbol
            const testOptions = {
                style: "currency",
                currency: "EUR",
                currencyDisplay: "narrowSymbol",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            };

            Intl.NumberFormat("en-EN", testOptions).format(99999.1234);
            return "narrowSymbol";
        } catch (error) {
            return "symbol";
        }
    }

    /**
     * Creates a new currency localization for a given currency ISO, using the details provided as arguments
     * and those at currency-config.json. Usually the result will be set to the BetModel.currencyLocalization
     *
     * @param {string} currencyISO The currency code string, e.g BGN, USD, EUR
     * @param {number} decimalPlaces The count of the numbers after the decimal separator (the currency precision).
     * This must be positive integer (or 0).
     * @return {*}  {ICurrencyLocalization} A new instance of ICurrencyLocalization class.
     * @memberof CurrencyUtils
     */
    public createCurrencyLocalization(
        currencyISO: string,
        symbol?: string,
        decimalPlaces?: number,
        currencyMultiplier?: number,
        country?: string
    ): ICurrencyLocalization {
        let result: ICurrency = undefined;
        let defaultMultiplier: number = currencyMultiplier || 1;
        let locale: string;
        try {
            if (symbol && this.specialCurrencies.some(e => e === symbol)) {
                locale = new Intl.NumberFormat('en-EN').resolvedOptions().locale;
            } else if (country) {
                locale = new Intl.NumberFormat(country).resolvedOptions().locale;
            } else {
                locale = "en-EN";
            }
        } catch (error) {
            locale = "en-EN";
        }

        if (!this.isValidCurrencyISO(currencyISO)) {
            // Probably this is a relax custom currency
            // like FUN or FC. or GC. or SC.
            // We will use the formatting for euro, but we keep the symbol as is.
            currencyISO = "EUR";
        }

        // Object.keys(currency_config).forEach((key) => {
        //     if (key == currencyISO) {
        //         let element = currency_config[key] as ICurrency;
        //         if (element && "symbol" in element) {
        //             if (currency_config[key].locale) {
        //                 if (!this.isValidLocale(currency_config[key].locale)) {
        //                     throw new Error(
        //                         "Invalid locale: " + currency_config[key].locale
        //                     );
        //                 }

        //                 locale = currency_config[key].locale;
        //             }

        //             if (decimalPlaces !== undefined && decimalPlaces > -1) {
        //                 element.decimals = decimalPlaces;
        //             }

        //             if (element.decimals === undefined) {
        //                 element.decimals = 2;
        //             }

        //             // Make sure we use a positive integer
        //             element.decimals = Math.max(
        //                 0,
        //                 Math.round(element.decimals)
        //             );

        //             // Assigning the result
        //             result = {
        //                 ISO: key,
        //                 decimals: element.decimals,
        //                 symbol: symbol || element.symbol,
        //             };

        //             if (!currencyMultiplier && currency_config[key].defaultMultiplier) {
        //                 defaultMultiplier = parseFloat(
        //                     currency_config[key].defaultMultiplier
        //                 );
        //             }
        //         }
        //     }
        // });

        if (!result) {
            // throw new Error("Not implemented currency: " + currencyISO);
            result = {
                ISO: currencyISO,
                symbol: symbol,
                decimals: decimalPlaces
            }
        }

        const testOptions = {
            style: "currency",
            currency: currencyISO,
            currencyDisplay: this.currencyDisplay,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        };

        let testOutput = new Intl.NumberFormat(locale, testOptions).format(
            99999.1234
        );

        const testSymbol = this.getCurrencySymbol(testOutput);
        if (testSymbol != result.symbol) {
            // We have a custom symbol
            testOutput = testOutput.replace(testSymbol, result.symbol);
        }

        // Make sure the symbol does not end with dot.
        testOutput = testOutput.replace(result.symbol + ".", "");

        const commaIndex = testOutput.replace(result.symbol, "").lastIndexOf(",");
        const dotIndex = testOutput.replace(result.symbol, "").lastIndexOf(".");
        let thousandsSeparator = "";
        if (commaIndex > -1 && dotIndex > -1) {
            thousandsSeparator = commaIndex > dotIndex ? "." : ",";
        }

        const format: ICurrencyFormatConf = {
            showIdentifier: true,
            identifierSpaceCount: 0,
            showSymbol: true,
            prependSymbol: this.shouldPrependSymbol(testOutput, result.symbol, result.ISO),
            decimalSeparatorSymbol: commaIndex > dotIndex ? "," : ".",
            spaceBetweenThousands: thousandsSeparator.length > 0,
            symbolBetweenThousands: thousandsSeparator,
        };

        this.runUnitTests();
        return {
            currency: result,
            format: format,
            defaultMultiplier: defaultMultiplier,
            locale: locale,
        };
    }

    /**
     * Converts a text to number for a given currency localization.
     *
     * @param {string} text The text to parse.
     * @param {ICurrencyLocalization} localization The currency localization to use. Usually this will be the BetModel.currencyLocalization
     * @return {*}  {number} A float number
     * @memberof CurrencyUtils
     */
    public parseCurrency(
        text: string,
        localization: ICurrencyLocalization
    ): number {
        if (!text) {
            return 0;
        }

        let numericPart = text
            .replace(localization.currency.symbol + ".", "")
            .replace(localization.currency.symbol + " ", "")
            .replace(localization.currency.symbol, "")
            .replace(localization.currency.ISO + " ", "")
            .replace(localization.currency.ISO, "")
            .replace(new RegExp(localization.format.symbolBetweenThousands, "g"), "")
            .replace(localization.format.decimalSeparatorSymbol, ".");

        let result = parseFloat(numericPart);
        return result;
    }

    /**
     * Converts a number to localized currency string.
     *
     * @param {number} value The money amount as float number.
     * @param {ICurrencyLocalization} localization The currency localization to use for the conversion. Usually this will be the BetModel.currencyLocalization
     * @param {boolean} [autoMultiply=true] Pass true to automatically multiply the input money value before the conversion is done.
     * @return {*}  {string} The formated (and premultiplied) currency string, according to the locale standard.
     * @memberof CurrencyUtils
     */
    public formatCurrency(
        value: number,
        localization: ICurrencyLocalization,
        autoMultiply: boolean = false
    ): string {
        const options = {
            style: "currency",
            currency: localization.currency.ISO,
            currencyDisplay: this.currencyDisplay,
            minimumFractionDigits: localization.currency.decimals,
            maximumFractionDigits: localization.currency.decimals,
        };

        if (autoMultiply) {
            value = this.multiplyCurrency(value, localization);
        }

        let result = new Intl.NumberFormat(localization.locale, options).format(
            value
        );
        if (result.endsWith(".")) {
            // Trimming the trailing comma
            result = result.substring(0, result.length - 1);
        }

        if (result.includes(localization.currency.ISO)) {
            result = result.replace(
                localization.currency.ISO,
                localization.currency.symbol
            );
        }

        if (!result.includes(localization.currency.symbol)) {
            // This is a custom symbol
            result = result.replace(this.getCurrencySymbol(result), localization.currency.symbol);
        }

        return result;
    }

    /**
     * Multiplies the input value by the currency localization default multiplier.
     *
     * @param {number} value The value to multiply.
     * @param {ICurrencyLocalization} localization The currency location to use. Usually this will be the BetModel.currencyLocalization
     * Usually this will be the BetModel.currencyLocalization
     * @return {*}  {number}
     * @memberof CurrencyUtils
     */
    public multiplyCurrency(
        value: number,
        localization: ICurrencyLocalization
    ): number {
        return new Big(value).mul(localization.defaultMultiplier).toNumber();
    }

    /**
     * Checks whether a given currency ISO is valid or not.
     *
     * @param {string} iso The iso to check. Valid iso examples: BGN, USD, EUR
     * @return {*}  {boolean} True if the iso is valid, otherwise false.
     * @memberof CurrencyUtils
     */
    public isValidCurrencyISO(iso: string): boolean {
        if (!iso) {
            return false;
        }

        try {
            new Intl.NumberFormat(undefined, { currency: iso }).format(1);
            return true;
        } catch (error) {
            return false;
        }
    }

    /**
     * Checks whether a given locale is valid or not.
     *
     * @param {string} locale The locale to check. Examples: bg-BG, en-US
     * @return {*} True if the locale is valid, otherwise false.
     * @memberof CurrencyUtils
     */
    public isValidLocale(locale: string): boolean {
        if (!locale) {
            return false;
        }

        try {
            new Intl.NumberFormat(locale).format(1);
            return true;
        } catch (error) {
            return false;
        }
    }

    private getCurrencySymbol(text: string) {
        let result: string = "";
        for (let i = 0; i < text.length; i++) {
            const char = text.charAt(i);
            if (isNaN(parseFloat(char)) && char != "." && char != ",") {
                result += char;
            }
        }

        result = result.trim();
        return result;
    }

    public runUnitTests(): void {
        if (this.parseCurrency("₫3,175,264", {
            currency: {
                ISO: "VND",
                decimals: 0,
                symbol: "₫"
            },
            defaultMultiplier: 20000,
            format: {
                decimalSeparatorSymbol: ".",
                identifierSpaceCount: 0,
                prependSymbol: true,
                showIdentifier: true,
                showSymbol: true,
                spaceBetweenThousands: false,
                symbolBetweenThousands: ","
            },
            locale: "en-US"
        }) != 3175264) {
            throw new Error("Unit test for 'VND' currency parse failed!");
        }
    }

    private shouldPrependSymbol(text: string, symbol: string, iso: string) {
        if (!this.isRightToLeft(text)) {
            return text.startsWith(symbol) || text.startsWith(iso);
        } else {
            return text.endsWith(symbol) || text.endsWith(iso);
        }
    }

    private isRightToLeft(text: string) {
        var ltrChars = 'A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02B8\u0300-\u0590\u0800-\u1FFF' + '\u2C00-\uFB1C\uFDFE-\uFE6F\uFEFD-\uFFFF',
            rtlChars = '\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC',
            rtlDirCheck = new RegExp('^[^' + ltrChars + ']*[' + rtlChars + ']');

        return rtlDirCheck.test(text);
    }
}

export default new CurrencyUtils();
